/**
 * Weapon firing, damage application, and projectile/VFX lifecycle for
 * the player robot. Extracted from `Robot.ts` as a companion class.
 *
 * `RobotWeaponry` owns the equipped-weapon roster, per-slot cooldown
 * and burst state, and the scratch buffer used to collect projectile
 * hits. Projectile / linger-zone / VFX arrays are held by reference —
 * they are still declared on `Robot` because `LabyrinthState` reads
 * them directly for rendering, and sharing the reference keeps both
 * sides in sync without a synchronising write-through.
 *
 * Methods that need chassis state (position, turret angle, side-
 * hardpoint targets, energy) take the `Robot` as their first argument
 * and read / mutate it via public fields. The coupling is deliberately
 * explicit so grep-based navigation still works after the split.
 */

import type { Labyrinth } from './Labyrinth';
import type { Robot, EquippedWeaponInfo, WeaponSlotRenderInfo } from './Robot';
import { type Enemy, getShieldMultiplier } from './Entity';
import {
  type Projectile, type VfxEffect, type EnemyProjectile, type LingerZone, type ProjectileHit,
  createBullet, createRocket, createBomb,
  createMuzzleFlash, createExplosion, createBeam, createMeleeSlash, createDeathExplosion,
  updateProjectiles, updateEnemyProjectiles, updateVfx,
} from './Projectile';
import { type DamageType, DAMAGE_TYPE_COLORS, applyStatusEffect } from './StatusEffect';
import { distSq, wrapAngle, pointToLineDistance } from './mathUtils';
import {
  TURRET_AIM_THRESHOLD,
  WEAPON_LATERAL_OFFSETS,
  BALLISTIC_FLASH_COLORS, ENERGY_COLORS, ROCKET_FLASH_COLORS,
  BOMB_FLASH_COLORS, MELEE_COLORS,
  WEAPON_SFX,
} from './RobotConstants';
import { generateLoot } from '../upgrades/UpgradeData';
import { generateWeaponLoot } from '../upgrades/WeaponData';
import { rollScore, rollBossScore, scoreBand } from '../upgrades/Quality';
import { generateItemName } from '../upgrades/ItemNames';
import type { SoundSystem } from '../audio/SoundSystem';
import { trackLootPickup } from '../achievements/Hooks';

/** Construction-time arguments for `RobotWeaponry`. */
export interface WeaponryInit {
  weapons: EquippedWeaponInfo[];
  weaponSlotCount: number;
  /** Shared mutable refs — Robot exposes these for rendering. */
  projectiles: Projectile[];
  enemyProjectiles: EnemyProjectile[];
  lingerZones: LingerZone[];
  vfx: VfxEffect[];
  sound: SoundSystem | null;
  labyrinth: Labyrinth;
}

export class RobotWeaponry {
  readonly weapons: EquippedWeaponInfo[];
  readonly weaponSlotCount: number;

  // Per-slot combat state (indexed by slot number)
  private readonly weaponCooldowns: number[];
  private readonly burstQueues: number[];
  private readonly burstTimers: number[];
  private static readonly BURST_INTERVAL = 0.08;

  // Shared refs with Robot
  private readonly projectiles: Projectile[];
  private readonly enemyProjectiles: EnemyProjectile[];
  private readonly lingerZones: LingerZone[];
  private readonly vfx: VfxEffect[];
  private readonly sound: SoundSystem | null;
  private readonly labyrinth: Labyrinth;

  // Weaponry-owned scratch / cooldown
  private readonly projectileHits: ProjectileHit[] = [];
  private explosionSoundCooldown = 0;

  constructor(init: WeaponryInit) {
    this.weapons = init.weapons;
    this.weaponSlotCount = init.weaponSlotCount;
    this.weaponCooldowns = this.weapons.map(() => 0);
    this.burstQueues = this.weapons.map(() => 0);
    this.burstTimers = this.weapons.map(() => 0);
    this.projectiles = init.projectiles;
    this.enemyProjectiles = init.enemyProjectiles;
    this.lingerZones = init.lingerZones;
    this.vfx = init.vfx;
    this.sound = init.sound;
    this.labyrinth = init.labyrinth;
  }

  // =======================================================
  // Queries — range, render info, slot helpers
  // =======================================================

  /** Longest-range equipped weapon, or unarmed melee fallback (0.8 tiles). */
  getEffectiveCombatRange(): number {
    if (this.weapons.length === 0) return 0.8;
    let maxRange = 0;
    for (const w of this.weapons) {
      if (w.range > maxRange) maxRange = w.range;
    }
    return maxRange;
  }

  /** Per-slot render info for the turret graphic (category + tier + cooldown). */
  getWeaponRenderSlots(): (WeaponSlotRenderInfo | null)[] {
    const slots: (WeaponSlotRenderInfo | null)[] = [];
    for (let i = 0; i < this.weaponSlotCount; i++) {
      slots.push(this.getHardpointSlotRender(i));
    }
    return slots;
  }

  /** Render info for a single weapon slot (null when empty). */
  getHardpointSlotRender(slotIndex: number): WeaponSlotRenderInfo | null {
    const w = this.weapons[slotIndex];
    if (!w) return null;
    const cd = this.weaponCooldowns[slotIndex] ?? 0;
    const progress = w.attackSpeed > 0 ? Math.max(0, Math.min(1, cd / w.attackSpeed)) : 0;
    return { category: w.category, tier: w.tier, cooldownProgress: progress };
  }

  /** Per-slot lateral offset (perpendicular to turret) in tile units. */
  private getWeaponLateralOffset(slotIndex: number): number {
    const offsets = WEAPON_LATERAL_OFFSETS[Math.min(this.weaponSlotCount, 4) - 1]!;
    return offsets[slotIndex] ?? 0;
  }

  /** Firing angle for a weapon slot — side hardpoints use their own angle. */
  private getWeaponAimAngle(robot: Robot, slotIndex: number): number {
    const sideIndices = robot.getSideSlotIndices();
    const sideIdx = sideIndices.indexOf(slotIndex);
    if (sideIdx >= 0) return robot.sideHardpointAngles[sideIdx]!;
    return robot.turretAngle;
  }

  /** Is this slot's aim within firing threshold of `targetAngle`? */
  private isWeaponAimed(robot: Robot, slotIndex: number, targetAngle: number): boolean {
    const aimAngle = this.getWeaponAimAngle(robot, slotIndex);
    return Math.abs(wrapAngle(targetAngle - aimAngle)) < TURRET_AIM_THRESHOLD;
  }

  /** Side-hardpoint index (0 or 1) for a slot, or -1 if not a side hardpoint. */
  private getSideHardpointIndex(robot: Robot, slotIndex: number): number {
    const sides = robot.getSideSlotIndices();
    return sides.indexOf(slotIndex);
  }

  /** Main-turret target falls through unless a side hardpoint has its own. */
  private resolveWeaponTarget(robot: Robot, slotIndex: number, mainEnemy: Enemy): Enemy {
    const sideIdx = this.getSideHardpointIndex(robot, slotIndex);
    if (sideIdx < 0) return mainEnemy;
    const sideTarget = robot.sideHardpointEnemyTargets[sideIdx];
    return sideTarget && sideTarget.alive ? sideTarget : mainEnemy;
  }

  // =======================================================
  // Fire — main enemy path
  // =======================================================

  /** Fire every equipped weapon that's off cooldown at the current main target. */
  fireWeapons(robot: Robot, dt: number, enemy: Enemy, turretAimed: boolean): void {
    const muzzleOffset = 0.35;

    for (let i = 0; i < this.weapons.length; i++) {
      const w = this.weapons[i]!;
      const lateral = this.getWeaponLateralOffset(i);
      const aimAngle = this.getWeaponAimAngle(robot, i);

      // Side hardpoints may fire at a different enemy (regular or swarm)
      const sideIdx = this.getSideHardpointIndex(robot, i);
      if (sideIdx >= 0) {
        const sideSwarmIdx = robot.sideHardpointSwarmTargets[sideIdx]!;
        if (sideSwarmIdx >= 0 && robot.swarmManager?.alive[sideSwarmIdx]) {
          const sm = robot.swarmManager;
          const stx = sm.x[sideSwarmIdx]!;
          const sty = sm.y[sideSwarmIdx]!;
          const sDist = Math.hypot(stx - robot.x, sty - robot.y);
          const sAngle = Math.atan2(sty - robot.y, stx - robot.x);

          if (this.burstQueues[i]! > 0) {
            this.burstTimers[i]! -= dt;
            if (this.burstTimers[i]! <= 0) {
              this.burstTimers[i] = RobotWeaponry.BURST_INTERVAL;
              this.burstQueues[i]!--;
              this.fireSwarmShot(robot, w, stx, sty, aimAngle);
            }
            continue;
          }
          this.weaponCooldowns[i]! -= dt;
          if (this.weaponCooldowns[i]! > 0) continue;
          if (sDist > w.range) continue;
          if (w.category !== 'melee' && !this.isWeaponAimed(robot, i, sAngle)) continue;
          if (w.energyCost > 0) {
            if (robot.energy < w.energyCost) continue;
            robot.energy -= w.energyCost;
          }
          this.weaponCooldowns[i] = w.attackSpeed;
          if (w.category === 'ballistic' && w.burstCount > 1) {
            this.burstQueues[i] = w.burstCount - 1;
            this.burstTimers[i] = RobotWeaponry.BURST_INTERVAL;
          }
          this.fireSwarmShot(robot, w, stx, sty, aimAngle);
          continue;
        }
      }

      const actualEnemy = this.resolveWeaponTarget(robot, i, enemy);
      const eDx = actualEnemy.x - robot.x;
      const eDy = actualEnemy.y - robot.y;
      const eDist = Math.hypot(eDx, eDy);
      const eTargetAngle = Math.atan2(eDy, eDx);

      // Process burst queue (remaining shots from a burst)
      if (this.burstQueues[i]! > 0) {
        this.burstTimers[i]! -= dt;
        if (this.burstTimers[i]! <= 0) {
          this.burstTimers[i] = RobotWeaponry.BURST_INTERVAL;
          this.burstQueues[i]!--;
          this.fireSingleShot(robot, w, actualEnemy, muzzleOffset, lateral, aimAngle);
        }
        continue;
      }

      this.weaponCooldowns[i]! -= dt;
      if (this.weaponCooldowns[i]! > 0) continue;
      if (eDist > w.range) continue;

      if (w.category !== 'melee') {
        const isSide = sideIdx >= 0;
        const aimed = isSide ? this.isWeaponAimed(robot, i, eTargetAngle) : turretAimed;
        if (!aimed) continue;
      }

      if (w.energyCost > 0) {
        if (robot.energy < w.energyCost) {
          robot.log(`Not enough energy (need ${w.energyCost})`, 0x6688aa);
          continue;
        }
        robot.energy -= w.energyCost;
      }

      this.weaponCooldowns[i] = w.attackSpeed;

      const catLabels: Record<string, string> = {
        ballistic: 'Firing', energy: 'Beam', rocket: 'Rocket', bomb: 'Bomb', melee: 'Strike',
      };
      const catColors: Record<string, number> = {
        ballistic: 0xffcc44, energy: 0x44ccff, rocket: 0xff8833, bomb: 0xff4400, melee: 0xffaa00,
      };
      const burstLabel = w.category === 'ballistic' && w.burstCount > 1 ? ` x${w.burstCount}` : '';
      robot.log(
        `${catLabels[w.category]}${burstLabel} → ${w.damage.toFixed(1)} dmg`,
        catColors[w.category] ?? 0xcccccc,
      );

      if (w.category === 'ballistic' && w.burstCount > 1) {
        this.burstQueues[i] = w.burstCount - 1;
        this.burstTimers[i] = RobotWeaponry.BURST_INTERVAL;
        this.fireSingleShot(robot, w, actualEnemy, muzzleOffset, lateral, aimAngle);
      } else {
        this.fireSingleShot(robot, w, actualEnemy, muzzleOffset, lateral, aimAngle);
      }
    }
  }

  /** Fire one shot — creates projectile or applies instant damage. */
  private fireSingleShot(
    robot: Robot, w: EquippedWeaponInfo, enemy: Enemy,
    muzzleOffset: number, lateralOffset: number = 0, overrideAngle?: number,
  ): void {
    const angle = overrideAngle ?? robot.turretAngle;
    const perpX = -Math.sin(angle) * lateralOffset;
    const perpY = Math.cos(angle) * lateralOffset;
    const mx = robot.x + Math.cos(angle) * muzzleOffset + perpX;
    const my = robot.y + Math.sin(angle) * muzzleOffset + perpY;

    const ti = w.tier - 1;
    const BALLISTIC_FLASH = BALLISTIC_FLASH_COLORS[ti] ?? 0xffcc44;
    const ENERGY_COLOR    = ENERGY_COLORS[ti]           ?? 0x44ccff;
    const ROCKET_FLASH    = ROCKET_FLASH_COLORS[ti]     ?? 0xff8833;
    const BOMB_FLASH      = BOMB_FLASH_COLORS[ti]       ?? 0xff4400;
    const MELEE_COLOR     = MELEE_COLORS[ti]            ?? 0xffaa00;

    void this.sound?.playSound(WEAPON_SFX[w.category]);

    const statusEffect = w.statusEffect ? {
      type: w.statusEffect.type,
      duration: w.statusEffect.duration,
      magnitude: w.statusEffect.magnitude,
      source: w.category,
    } : undefined;

    switch (w.category) {
      case 'ballistic': {
        const baseSpread = w.spreadAngle > 0 ? w.spreadAngle : (0.08 - w.tier * 0.01);
        const spread = (Math.random() - 0.5) * baseSpread;
        this.projectiles.push(createBullet(mx, my, angle + spread, w.damage, w.range, w.tier, {
          pierceCount: w.pierceCount,
          knockback: w.knockback,
          fragmentCount: w.fragmentCount,
          bonusDamageMult: w.bonusDamageMult,
          speedMult: w.speedMult,
          statusEffect,
        }));
        this.vfx.push(createMuzzleFlash(mx, my, BALLISTIC_FLASH));
        break;
      }
      case 'energy': {
        const dmg = w.damage * w.bonusDamageMult;

        if (w.pierceCount > 0) {
          const beamEndX = mx + Math.cos(angle) * w.range;
          const beamEndY = my + Math.sin(angle) * w.range;
          for (const e of robot.aliveEnemies) {
            if (!e.alive) continue;
            if (pointToLineDistance(e.x, e.y, mx, my, beamEndX, beamEndY) < 0.4) {
              this.applyDamageToEnemy(robot, e, dmg, 'energy');
              this.applyWeaponEffects(e, w);
            }
          }
          this.vfx.push(createBeam(mx, my, beamEndX, beamEndY, ENERGY_COLOR));
        } else {
          this.applyDamageToEnemy(robot, enemy, dmg, 'energy');
          this.applyWeaponEffects(enemy, w);
          this.vfx.push(createBeam(mx, my, enemy.x, enemy.y, ENERGY_COLOR));
        }

        if (w.splashRadius > 0) {
          for (const e of robot.aliveEnemies) {
            if (e === enemy || !e.alive) continue;
            const sd = Math.hypot(e.x - enemy.x, e.y - enemy.y);
            if (sd <= w.splashRadius) {
              this.applyDamageToEnemy(robot, e, dmg * 0.5, 'energy');
              this.applyWeaponEffects(e, w);
            }
          }
          this.vfx.push(createExplosion(enemy.x, enemy.y, w.splashRadius, ENERGY_COLOR));
        }

        this.vfx.push(createMuzzleFlash(mx, my, ENERGY_COLOR));
        break;
      }
      case 'rocket': {
        const count = w.projectileCount;
        const dmgPer = w.damage / count;
        const baseSpread = 0.15;
        for (let n = 0; n < count; n++) {
          const spreadAngle = count > 1
            ? angle + (n - (count - 1) / 2) * baseSpread
            : angle;
          this.projectiles.push(
            createRocket(mx, my, spreadAngle, dmgPer, w.range, w.aoeRadius, w.knockback, w.tier, {
              homingStrength: w.homingStrength,
              homingTarget: enemy,
              speedMult: w.speedMult,
              lingerDuration: w.lingerDuration,
              lingerDps: w.lingerDps,
              statusEffect,
            }),
          );
        }
        this.vfx.push(createMuzzleFlash(mx, my, ROCKET_FLASH));
        break;
      }
      case 'bomb': {
        this.projectiles.push(
          createBomb(mx, my, enemy.x, enemy.y, w.damage, w.aoeRadius, w.tier, {
            knockback: w.knockback,
            pullStrength: w.pullStrength,
            damageFalloff: w.damageFalloff,
            lingerDuration: w.lingerDuration,
            lingerDps: w.lingerDps,
            statusEffect,
          }),
        );
        this.vfx.push(createMuzzleFlash(mx, my, BOMB_FLASH));
        break;
      }
      case 'melee': {
        const strikes = w.doubleStrike ? 2 : 1;
        const dmg = w.damage * w.bonusDamageMult;
        for (let s = 0; s < strikes; s++) {
          this.applyDamageToEnemy(robot, enemy, dmg, 'melee');
          this.applyWeaponEffects(enemy, w);
        }
        this.vfx.push(createMeleeSlash(enemy.x, enemy.y, MELEE_COLOR));
        if (w.doubleStrike) {
          this.vfx.push(createMeleeSlash(enemy.x + 0.15, enemy.y - 0.1, MELEE_COLOR));
        }
        if (w.knockback > 0) {
          this.applyKnockback(enemy, robot.x, robot.y, w.knockback);
        }
        if (w.aoeRadius > 0) {
          for (const e of robot.aliveEnemies) {
            if (e === enemy || !e.alive) continue;
            const ad = Math.hypot(e.x - enemy.x, e.y - enemy.y);
            if (ad <= w.aoeRadius) {
              this.applyDamageToEnemy(robot, e, w.damage * 0.5, 'melee');
              this.applyWeaponEffects(e, w);
              if (w.knockback > 0) this.applyKnockback(e, robot.x, robot.y, w.knockback);
            }
          }
          this.vfx.push(createExplosion(enemy.x, enemy.y, w.aoeRadius, MELEE_COLOR));
        }
        break;
      }
    }
  }

  // =======================================================
  // Fire — swarm enemy path
  // =======================================================

  /** Fire weapons at a swarm enemy. Hitscan / melee damage applied directly. */
  fireSwarmWeapons(robot: Robot, dt: number, swarmIdx: number, turretAimed: boolean): void {
    const sm = robot.swarmManager!;

    for (let i = 0; i < this.weapons.length; i++) {
      const w = this.weapons[i]!;
      const aimAngle = this.getWeaponAimAngle(robot, i);

      // Side hardpoints may target a different swarm or regular enemy.
      const sideIdx = this.getSideHardpointIndex(robot, i);
      let actualIdx = swarmIdx;
      if (sideIdx >= 0) {
        const sideSwarmIdx = robot.sideHardpointSwarmTargets[sideIdx]!;
        if (sideSwarmIdx >= 0 && sm.alive[sideSwarmIdx]) {
          actualIdx = sideSwarmIdx;
        }
        const sideEnemy = robot.sideHardpointEnemyTargets[sideIdx]!;
        if (sideEnemy && sideEnemy.alive) {
          // Delegate to the regular-enemy shot path for this hardpoint.
          const eDist = Math.hypot(sideEnemy.x - robot.x, sideEnemy.y - robot.y);
          const eAngle = Math.atan2(sideEnemy.y - robot.y, sideEnemy.x - robot.x);

          if (this.burstQueues[i]! > 0) {
            this.burstTimers[i]! -= dt;
            if (this.burstTimers[i]! <= 0) {
              this.burstTimers[i] = RobotWeaponry.BURST_INTERVAL;
              this.burstQueues[i]!--;
              this.fireSingleShot(robot, w, sideEnemy, 0.35, this.getWeaponLateralOffset(i), aimAngle);
            }
            continue;
          }
          this.weaponCooldowns[i]! -= dt;
          if (this.weaponCooldowns[i]! > 0) continue;
          if (eDist > w.range) continue;
          if (w.category !== 'melee' && !this.isWeaponAimed(robot, i, eAngle)) continue;
          if (w.energyCost > 0) {
            if (robot.energy < w.energyCost) continue;
            robot.energy -= w.energyCost;
          }
          this.weaponCooldowns[i] = w.attackSpeed;
          if (w.category === 'ballistic' && w.burstCount > 1) {
            this.burstQueues[i] = w.burstCount - 1;
            this.burstTimers[i] = RobotWeaponry.BURST_INTERVAL;
          }
          this.fireSingleShot(robot, w, sideEnemy, 0.35, this.getWeaponLateralOffset(i), aimAngle);
          continue;
        }
      }

      const stx = sm.x[actualIdx]!;
      const sty = sm.y[actualIdx]!;
      const sDist = Math.hypot(stx - robot.x, sty - robot.y);
      const sTargetAngle = Math.atan2(sty - robot.y, stx - robot.x);

      if (this.burstQueues[i]! > 0) {
        this.burstTimers[i]! -= dt;
        if (this.burstTimers[i]! <= 0) {
          this.burstTimers[i] = RobotWeaponry.BURST_INTERVAL;
          this.burstQueues[i]!--;
          this.fireSwarmShot(robot, w, stx, sty, aimAngle);
        }
        continue;
      }

      this.weaponCooldowns[i]! -= dt;
      if (this.weaponCooldowns[i]! > 0) continue;
      if (sDist > w.range) continue;

      if (w.category !== 'melee') {
        const isSide = robot.isSideHardpoint(i);
        const aimed = isSide ? this.isWeaponAimed(robot, i, sTargetAngle) : turretAimed;
        if (!aimed) continue;
      }

      if (w.energyCost > 0) {
        if (robot.energy < w.energyCost) continue;
        robot.energy -= w.energyCost;
      }

      this.weaponCooldowns[i] = w.attackSpeed;

      if (w.category === 'ballistic' && w.burstCount > 1) {
        this.burstQueues[i] = w.burstCount - 1;
        this.burstTimers[i] = RobotWeaponry.BURST_INTERVAL;
      }

      this.fireSwarmShot(robot, w, stx, sty, aimAngle);
    }
  }

  /** Fire a single shot at a swarm enemy position (world-space tx/ty). */
  private fireSwarmShot(
    robot: Robot, w: EquippedWeaponInfo, tx: number, ty: number, overrideAngle?: number,
  ): void {
    const angle = overrideAngle ?? robot.turretAngle;
    const mx = robot.x + Math.cos(angle) * 0.5;
    const my = robot.y + Math.sin(angle) * 0.5;

    const statusEffect = w.statusEffect ? {
      type: w.statusEffect.type,
      duration: w.statusEffect.duration,
      magnitude: w.statusEffect.magnitude,
      source: w.category,
    } : undefined;

    void this.sound?.playSound(WEAPON_SFX[w.category]);

    const ti = w.tier - 1;

    switch (w.category) {
      case 'ballistic': {
        const spread = (Math.random() - 0.5) * (w.spreadAngle > 0 ? w.spreadAngle : 0.08 - w.tier * 0.01);
        this.projectiles.push(createBullet(mx, my, angle + spread, w.damage, w.range, w.tier, {
          pierceCount: w.pierceCount, knockback: w.knockback,
          fragmentCount: w.fragmentCount, bonusDamageMult: w.bonusDamageMult,
          speedMult: w.speedMult, statusEffect,
        }));
        this.vfx.push(createMuzzleFlash(mx, my, BALLISTIC_FLASH_COLORS[ti] ?? 0xffcc44));
        break;
      }
      case 'energy': {
        const sm = robot.swarmManager!;
        const dmg = w.damage * w.bonusDamageMult;
        const beamEndX = mx + Math.cos(angle) * w.range;
        const beamEndY = my + Math.sin(angle) * w.range;

        const candidates = sm.grid.query((mx + beamEndX) / 2, (my + beamEndY) / 2, w.range);
        for (const ci of candidates) {
          if (!sm.alive[ci]) continue;
          if (pointToLineDistance(sm.x[ci]!, sm.y[ci]!, mx, my, beamEndX, beamEndY) < 0.4) {
            sm.damage_enemy(ci, dmg);
          }
        }
        if (w.pierceCount > 0) {
          for (const e of robot.aliveEnemies) {
            if (!e.alive) continue;
            if (pointToLineDistance(e.x, e.y, mx, my, beamEndX, beamEndY) < 0.4) {
              this.applyDamageToEnemy(robot, e, dmg, 'energy');
              this.applyWeaponEffects(e, w);
            }
          }
        }
        this.vfx.push(createBeam(mx, my, beamEndX, beamEndY, ENERGY_COLORS[ti] ?? 0x44ccff));
        this.vfx.push(createMuzzleFlash(mx, my, ENERGY_COLORS[ti] ?? 0x44ccff));
        break;
      }
      case 'rocket': {
        const count = w.projectileCount;
        const dmgPer = w.damage / count;
        const baseSpread = 0.15;
        for (let n = 0; n < count; n++) {
          const spreadAngle = count > 1
            ? angle + (n - (count - 1) / 2) * baseSpread
            : angle;
          this.projectiles.push(
            createRocket(mx, my, spreadAngle, dmgPer, w.range, w.aoeRadius, w.knockback, w.tier, {
              homingStrength: 0,
              speedMult: w.speedMult,
              lingerDuration: w.lingerDuration, lingerDps: w.lingerDps,
              statusEffect,
            }),
          );
        }
        this.vfx.push(createMuzzleFlash(mx, my, ROCKET_FLASH_COLORS[ti] ?? 0xff8833));
        break;
      }
      case 'bomb': {
        this.projectiles.push(
          createBomb(mx, my, tx, ty, w.damage, w.aoeRadius, w.tier, {
            knockback: w.knockback, pullStrength: w.pullStrength,
            damageFalloff: w.damageFalloff,
            lingerDuration: w.lingerDuration, lingerDps: w.lingerDps,
            statusEffect,
          }),
        );
        this.vfx.push(createMuzzleFlash(mx, my, BOMB_FLASH_COLORS[ti] ?? 0xff4400));
        break;
      }
      case 'melee': {
        const sm = robot.swarmManager!;
        const strikes = w.doubleStrike ? 2 : 1;
        const dmg = w.damage * w.bonusDamageMult;
        for (let s = 0; s < strikes; s++) {
          sm.damage_enemy(robot.swarmTargetIdx, dmg);
          if (w.aoeRadius > 0) {
            const nearby = sm.grid.query(tx, ty, w.aoeRadius);
            for (const ni of nearby) {
              if (ni === robot.swarmTargetIdx || !sm.alive[ni]) continue;
              const d = Math.hypot(sm.x[ni]! - tx, sm.y[ni]! - ty);
              if (d <= w.aoeRadius) sm.damage_enemy(ni, dmg * 0.5);
            }
            for (const e of robot.aliveEnemies) {
              if (!e.alive) continue;
              const ad = Math.hypot(e.x - tx, e.y - ty);
              if (ad <= w.aoeRadius) {
                this.applyDamageToEnemy(robot, e, dmg * 0.5, 'melee');
              }
            }
          }
        }
        this.vfx.push(createMeleeSlash(tx, ty, MELEE_COLORS[ti] ?? 0xffaa00));
        break;
      }
    }
  }

  // =======================================================
  // Damage application
  // =======================================================

  /** Apply a weapon's status effect to `enemy`, if one is configured. */
  private applyWeaponEffects(enemy: Enemy, w: EquippedWeaponInfo): void {
    if (!enemy.alive || !w.statusEffect) return;
    applyStatusEffect(enemy, {
      type: w.statusEffect.type,
      remaining: w.statusEffect.duration,
      duration: w.statusEffect.duration,
      magnitude: w.statusEffect.magnitude,
      source: w.category,
    });
  }

  /**
   * Deal `damage` to `enemy` (after shielder-aura attenuation), spawn
   * damage/XP floating text, and handle death: XP + kill count, death
   * VFX/SFX, loot rolls (weapon + upgrade, quality-scaled per-boss),
   * and — if this was the current combat target — clear it so the
   * state machine can disengage.
   */
  applyDamageToEnemy(
    robot: Robot, enemy: Enemy, damage: number, damageType: DamageType = 'ballistic',
  ): void {
    const shieldMult = getShieldMultiplier(enemy, robot.aliveShielders);
    if (shieldMult < 1) {
      damage *= shieldMult;
      robot.floatText('shielded', 0x44aaff, enemy.x, enemy.y - 0.5);
    }

    const jitterX = (Math.random() - 0.5) * 0.4;
    const textColor = DAMAGE_TYPE_COLORS[damageType];
    robot.floatText(`${Math.round(damage)}`, textColor, enemy.x + jitterX, enemy.y);
    enemy.hp -= damage;

    // Achievement: damage dealt + biggest single hit
    {
      const ach = robot.achievements;
      if (ach) {
        ach.stats.totalDamageDealt += damage;
        if (damage > ach.stats.biggestHit) ach.stats.biggestHit = damage;
      }
    }

    if (enemy.hp <= 0) {
      enemy.hp = 0;
      enemy.alive = false;
      robot.xpGained += enemy.xp;
      robot.killCount++;

      // Achievement: kill attribution.
      {
        const ach = robot.achievements;
        if (ach) {
          const s = ach.stats;
          s.totalKills++;
          s.killsByType[enemy.enemyKind]++;
          s.killsByCategory[damageType]++;
          if (enemy.isBoss) s.bossKills++;
          ach.recheck();
        }
      }
      const label = enemy.isBoss ? 'BOSS destroyed!' : 'Enemy destroyed!';
      robot.log(`${label} +${enemy.xp} XP`, enemy.isBoss ? 0xffcc00 : 0x44ff88);
      robot.floatText(
        `+${enemy.xp} XP`, enemy.isBoss ? 0xffcc00 : 0x44ff88,
        enemy.x, enemy.y - 0.3, enemy.isBoss,
      );
      const deathColor = DAMAGE_TYPE_COLORS[damageType] ?? 0xff6600;
      if (enemy.isBoss) {
        this.vfx.push(createDeathExplosion(enemy.x, enemy.y, 0xffcc00));
        this.vfx.push(createDeathExplosion(enemy.x, enemy.y, deathColor));
      } else {
        this.vfx.push(createDeathExplosion(enemy.x, enemy.y, deathColor));
      }
      if (this.explosionSoundCooldown <= 0) {
        void this.sound?.playSound('/sound/explosion.mp3', enemy.isBoss ? 3.0 : 1.5);
        this.explosionSoundCooldown = 0.15;
      }

      const lootDist = Math.abs(enemy.x - robot.baseX) + Math.abs(enemy.y - robot.baseY);
      const lootChance = enemy.isBoss ? 1.0 : 0.5;
      const qualityBoost = enemy.isBoss ? 0.6 : 0.4;
      if (Math.random() < lootChance) {
        const drop = generateWeaponLoot(lootDist, qualityBoost);
        if (drop) {
          const score = enemy.isBoss ? rollBossScore() : rollScore(lootDist, robot.qualityBonus);
          const name = generateItemName('weapon', drop.category, score);
          const band = scoreBand(score);
          robot.lootBag.push({ def: drop, item: { id: drop.id, score, name } });
          robot.log(`Loot: ${name} (${band.label} · ${score})`, band.color);
          trackLootPickup(robot, score, enemy.isBoss, drop.id);
        }
      }
      if (Math.random() < lootChance) {
        const drop = generateLoot(lootDist, qualityBoost);
        if (drop) {
          const score = enemy.isBoss ? rollBossScore() : rollScore(lootDist, robot.qualityBonus);
          const name = generateItemName('upgrade', drop.category, score);
          const band = scoreBand(score);
          robot.lootBag.push({ def: drop, item: { id: drop.id, score, name } });
          robot.log(`Loot: ${name} (${band.label} · ${score})`, band.color);
          trackLootPickup(robot, score, enemy.isBoss, null);
        }
      }

      if (robot.target === enemy) {
        robot.state = robot.marker ? 'moving_to_marker' : 'exploring';
        robot.target = null;
        enemy.attackTimer = 0;
        robot.nav.clearPath();
      }
    }
  }

  /** Push an enemy away from `(originX, originY)` by `amount` tiles, only onto walkable cells. */
  applyKnockback(enemy: Enemy, originX: number, originY: number, amount: number): void {
    const dx = enemy.x - originX;
    const dy = enemy.y - originY;
    const d = Math.hypot(dx, dy);
    if (d < 0.01) return;
    const nx = dx / d;
    const ny = dy / d;
    const newX = enemy.x + nx * amount;
    const newY = enemy.y + ny * amount;
    if (this.labyrinth.isWalkable(Math.floor(newX), Math.floor(newY))) {
      enemy.x = newX;
      enemy.y = newY;
    }
  }

  // =======================================================
  // Projectile + VFX tick
  // =======================================================

  /**
   * Advance every projectile, enemy projectile, VFX, and linger zone.
   * Applies damage + knockback + status effects + fragment spawning
   * from projectile hits, and mutates `robot.structure` / `robot.energy`
   * on enemy-projectile hits to the robot itself.
   */
  updateProjectilesAndVfx(robot: Robot, dt: number): void {
    // Safety caps — prevent unbounded growth (trim oldest to avoid O(n) shift).
    if (this.projectiles.length > 200)      this.projectiles.length = 200;
    if (this.enemyProjectiles.length > 100) this.enemyProjectiles.length = 100;
    if (this.vfx.length > 100)              this.vfx.length = 100;
    if (this.lingerZones.length > 20)       this.lingerZones.length = 20;

    const enemies = robot.aliveEnemies;

    this.explosionSoundCooldown = Math.max(0, this.explosionSoundCooldown - dt);

    const hits = updateProjectiles(
      this.projectiles, dt, enemies, this.labyrinth, this.vfx, this.lingerZones,
      this.projectileHits, robot.swarmManager,
    );

    for (const hit of hits) {
      const dmg = hit.projectile.damage * hit.projectile.bonusDamageMult * hit.damageMultiplier;
      this.applyDamageToEnemy(robot, hit.enemy, dmg, hit.projectile.damageType);

      if (hit.projectile.statusEffect && hit.enemy.alive) {
        applyStatusEffect(hit.enemy, {
          type: hit.projectile.statusEffect.type,
          remaining: hit.projectile.statusEffect.duration,
          duration: hit.projectile.statusEffect.duration,
          magnitude: hit.projectile.statusEffect.magnitude,
          source: hit.projectile.statusEffect.source,
        });
      }

      if (hit.projectile.knockback > 0) {
        this.applyKnockback(hit.enemy, robot.x, robot.y, hit.projectile.knockback);
      }
      if (hit.projectile.aoeRadius > 0 && this.explosionSoundCooldown <= 0) {
        void this.sound?.playSound('/sound/explosion.mp3', 1.5);
        this.explosionSoundCooldown = 0.15;
      }

      if (hit.projectile.fragmentCount > 0) {
        for (let f = 0; f < hit.projectile.fragmentCount; f++) {
          const fragAngle = Math.random() * Math.PI * 2;
          this.projectiles.push(
            createBullet(hit.x, hit.y, fragAngle, hit.projectile.damage * 0.4, 2.0, hit.projectile.tier),
          );
        }
        hit.projectile.fragmentCount = 0;
      }
    }

    // Tick linger zones (ground DoT patches).
    for (let i = this.lingerZones.length - 1; i >= 0; i--) {
      const z = this.lingerZones[i]!;
      z.remaining -= dt;
      if (z.remaining <= 0) { this.lingerZones.splice(i, 1); continue; }
      z.tickTimer += dt;
      if (z.tickTimer >= 0.5) {
        z.tickTimer -= 0.5;
        const rSq = z.radius * z.radius;
        for (const e of enemies) {
          if (!e.alive) continue;
          if (distSq(e.x, e.y, z.x, z.y) <= rSq) {
            this.applyDamageToEnemy(robot, e, z.dps * 0.5, z.damageType);
          }
        }
        if (robot.swarmManager && robot.swarmManager.aliveCount > 0) {
          const nearby = robot.swarmManager.grid.query(z.x, z.y, z.radius);
          for (let n = 0; n < nearby.length; n++) {
            const si = nearby[n]!;
            if (!robot.swarmManager.alive[si]) continue;
            if (distSq(robot.swarmManager.x[si]!, robot.swarmManager.y[si]!, z.x, z.y) <= rSq) {
              robot.swarmManager.damage_enemy(si, z.dps * 0.5);
            }
          }
        }
      }
    }

    // Enemy projectiles → damage robot.
    const enemyDmg = updateEnemyProjectiles(
      this.enemyProjectiles, dt, robot.x, robot.y, this.labyrinth, this.vfx,
    );
    if (enemyDmg > 0) {
      const mitigated = enemyDmg * (1 - robot.damageReduction);
      robot.structure -= mitigated;
      robot.damageTakenThisRun += mitigated;
      if (robot.structure < robot.maxStructure * 0.1) robot.lowHpEverThisRun = true;
      const blocked = enemyDmg - mitigated;
      robot.log(
        `Hit! -${mitigated.toFixed(1)} HP` + (blocked > 0.1 ? ` (${blocked.toFixed(1)} blocked)` : ''),
        0xff4444,
      );
      robot.floatText(`-${mitigated.toFixed(1)}`, 0xff4444, robot.x, robot.y);
      if (robot.structure <= 0) {
        robot.structure = 0;
        robot.energy = 0;
        robot.log('DESTROYED — emergency recall', 0xff0000);
      }
    }

    updateVfx(this.vfx, dt);
  }
}
